<template>
  <b-modal v-model="model" centered modal-class="modal-bb-squared" hide-footer>
    <template slot="modal-header" slot-scope="{ close }">
      <h5>Apply for this position</h5>

      <p class="w-75 fs15 text-gray-700 pt-10">
        The following is required and will only be shared with
        {{ job.company.name }}
      </p>
      <button class="close" @click="close()"></button>
    </template>

    <template slot="default">
      <form @submit.prevent="onSubmit">
        <FormItem
          class="mb-10"
          invalid-message="Please enter name"
          :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
          required
        >
          <b-form-input
            v-model="form.name"
            maxlength="100"
            :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
            placeholder="Your name"
          />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter email"
          :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
          required
        >
          <b-form-input
            v-model="form.email"
            :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
            placeholder="Email"
          />
        </FormItem>

        <FormItem
          class="mb-10"
          invalid-message="Please enter phone"
          :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
          required
        >
          <b-form-input
            v-model="form.phone"
            :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
            placeholder="Phone number"
          />
        </FormItem>

        <div class="border-bottom my-20"></div>

        <FormItem class="mb-10">
          <b-form-input v-model="form.title" placeholder="Current or previous job title" />
        </FormItem>

        <FormItem
          v-if="isRequired('linkedin')"
          class="mb-10"
          invalid-message="Please enter link to LinkedIn profile"
          :state="$v.form.linkedin.$dirty ? !$v.form.linkedin.$error : null"
          required
        >
          <b-form-input
            v-model="form.linkedin"
            class="has-icon linkedin-icon"
            :state="$v.form.linkedin.$dirty ? !$v.form.linkedin.$error : null"
            placeholder="Link to your LinkedIn profile"
          />
        </FormItem>

        <FormItem
          v-if="isRequired('github')"
          class="mb-10"
          invalid-message="Please enter github"
          :state="$v.form.github.$dirty ? !$v.form.github.$error : null"
          required
        >
          <b-form-input
            v-model="form.github"
            class="has-icon github-icon"
            :state="$v.form.github.$dirty ? !$v.form.github.$error : null"
            placeholder="Link to your Github"
          />
        </FormItem>

        <FormItem
          v-if="isRequired('portfolio')"
          class="mb-10"
          invalid-message="Please enter portfolio"
          :state="$v.form.portfolioLink.$dirty ? !$v.form.portfolioLink.$error : null"
          required
        >
          <b-form-input
            v-model="form.portfolioLink"
            :state="$v.form.portfolioLink.$dirty ? !$v.form.portfolioLink.$error : null"
            placeholder="Link to your portfolio"
          />
        </FormItem>

        <FormItem
          v-if="isRequired('text') && !questions.length"
          class="mb-10"
          invalid-message="Please enter message"
          :state="$v.form.message.$dirty ? !$v.form.message.$error : null"
          required
        >
          <template slot="description">
            <FormInputLengthCounter
              label="Maximum 500 characters"
              exceeded-label="Keep it short, no one has time to read a novel"
              exceeded-label-color="#FA6400"
              :max="500"
              :text="form.message"
            />
          </template>

          <template>
            <b-form-textarea
              v-model="form.message"
              rows="3"
              :max-rows="8"
              placeholder="Write a short message to the startup"
              :state="$v.form.message.$dirty ? !$v.form.message.$error : null"
              @blur="$v.form.message.$touch()"
            />
          </template>
        </FormItem>

        <FormItem
          v-for="(question, i) in questions"
          :key="question.id"
          class="mb-10"
          invalid-message="Please enter answer"
          :state="$v.form.answers.$each[i].$dirty ? !$v.form.answers.$each[i].$error : null"
          required
        >
          <template slot="description">
            <FormInputLengthCounter
              label="Maximum 500 characters"
              exceeded-label="Keep it short, no one has time to read a novel"
              exceeded-label-color="#FA6400"
              :max="500"
              :text="form.answers[i].answer"
            />
          </template>

          <template>
            <b-form-textarea
              v-model="form.answers[i].answer"
              v-b-tooltip.hover
              :title="form.answers[i].answer ? question.question : null"
              rows="1"
              :max-rows="4"
              :placeholder="question.question"
              :state="
                $v.form.answers.$each[i].answer.$dirty
                  ? !$v.form.answers.$each[i].answer.$error
                  : null
              "
              @blur="$v.form.answers.$each[i].answer.$touch()"
            />
          </template>
        </FormItem>

        <FormItem
          v-if="isRequired('cv')"
          class="py-16 mb-0 border-bottom"
          invalid-message="Please attach resume (Max. 10MB)"
          :state="$v.form.cv.$dirty ? !$v.form.cv.$error : null"
          required
        >
          <FormUploadFile ref="cv" v-model="form.cv" type="document" label="Attach your resume" />
        </FormItem>

        <div class="my-16"></div>

        <FormItem
          v-if="isRequired('video')"
          class="mb-10 py-16"
          invalid-message="Please enter video link"
          label="Short video about yourself"
          :state="$v.form.videoLink.$dirty ? !$v.form.videoLink.$error : null"
        >
          <b-form-input
            v-model="form.videoLink"
            class="has-icon video-icon mt-20"
            :state="$v.form.videoLink.$dirty ? !$v.form.videoLink.$error : null"
            placeholder="Link to your video"
          />
        </FormItem>

        <FormItem
          v-if="isRequired('video')"
          class="pb-16 mb-0 border-bottom"
          invalid-message="Please attach video (Max. 50MB)"
          :state="$v.form.video.$dirty ? !$v.form.video.$error : null"
          required
        >
          <FormUploadFile ref="video" v-model="form.video" type="video" label="or attach a video" />
          <span style="display:block; max-width: 250px;" class="text-gray-700 lh14 fs13"
            >Maximum size 50 mb equal to 20-25 secs recorded on phone in HD</span
          >
          <SlideUpDown class="form-item__error" :duration="125" :active="isVideoInvalid">
            <div class="form-item__error__inner">
              You can only link OR attach a video. Kindly chose one of the options
            </div>
          </SlideUpDown>
        </FormItem>

        <FormItem class="mt-20">
          <b-form-checkbox v-model="form.updates">
            <div class="fs14 mt-1 text-gray-700">
              Send me email updates with similar positions
            </div>
          </b-form-checkbox>
        </FormItem>

        <FormItem
          class="my-30"
          :state="$v.form.reCaptchaResponse.$dirty ? !$v.form.reCaptchaResponse.$error : null"
          invalid-message="Please verify you are not a robot"
        >
          <VueRecaptcha
            ref="reCaptcha"
            :sitekey="siteKey"
            :load-recaptcha-script="true"
            @verify="onVerify"
            @expired="onExpired"
          />
        </FormItem>

        <AlertSlideUpDown :show="!!errorMessage">
          {{ errorMessage }}
        </AlertSlideUpDown>

        <b-button type="submit" class="btn btn-lg w-100 mb-20">
          <span v-if="!isRequesting">Apply</span>
          <b-spinner v-else small />
        </b-button>

        <div class="fs14 mt-10">
          By sending the request you confirm that you accept our
          <router-link to="/terms" target="_blank" class="text-blue-900"
            >Terms of Service</router-link
          >
          and
          <router-link to="/privacy-policy" target="_blank" class="text-blue-900"
            >Privacy Policy</router-link
          >.
        </div>
      </form>
    </template>
  </b-modal>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { validationMixin } from 'vuelidate';
import { required, requiredIf, email } from 'vuelidate/lib/validators';
import { linkedinUrlValidator, urlValidator } from '@/validators/urlValidator';
import FormItem from '@/components/form/FormItem';
import FormInputLengthCounter from '@/components/form/FormInputLengthCounter';
import AlertSlideUpDown from '@/components/AlertSlideUpDown';
import SlideUpDown from 'vue-slide-up-down';
import FormUploadFile from '@/components/form/FormUploadFile/index';

const initial = {
  name: undefined,
  email: undefined,
  phone: undefined,
  title: undefined,
  linkedin: undefined,
  portfolioLink: undefined,
  videoLink: undefined,
  github: undefined,
  message: undefined,
  cv: undefined,
  video: undefined,
  updates: true,
  reCaptchaResponse: undefined
};

export default {
  components: {
    VueRecaptcha,
    AlertSlideUpDown,
    FormItem,
    FormInputLengthCounter,
    FormUploadFile,
    SlideUpDown
  },
  mixins: [validationMixin],
  props: {
    job: {
      required: true,
      type: Object
    },
    value: {
      required: true,
      type: Boolean
    }
  },
  data: () => ({
    siteKey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
    isRequesting: false,
    errorMessage: '',

    form: {
      ...initial,
      answers: []
    }
  }),
  computed: {
    user() {
      return this.$store.getters['user/data'];
    },
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    questions() {
      if (!this.job.criterias.includes('text')) {
        return [];
      }

      return this.job.questions
        .filter((item) => item.isEnabled)
        .sort((itemA, itemB) => itemA.position - itemB.position);
    },
    isVideoInvalid() {
      if (this.job.criterias.includes('video')) {
        let videos = [];
        if (this.form.video) {
          videos.push(this.form.video);
        }
        if (this.form.videoLink) {
          videos.push(this.form.videoLink);
        }
        if (videos.length > 1) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    value() {
      if (!this.value) {
        // on modal hide, reset re-captcha
        this.$refs.reCaptcha.reset();
      }
    },
    questions: {
      immediate: true,
      handler(value) {
        this.form.answers = value.map((item) => ({
          question: item.id,
          answer: ''
        }));
      }
    }
  },
  validations() {
    return {
      form: {
        email: {
          required,
          email
        },
        name: {
          required
        },
        phone: {
          required
        },
        linkedin: this.isRequired('linkedin')
          ? {
              required,
              linkedinUrlValidator
            }
          : {},
        portfolioLink: this.isRequired('portfolio') ? { required } : {},
        github: this.isRequired('github') ? { required } : {},
        message: this.isRequired('text') && !this.questions.length ? { required } : {},
        cv: this.isRequired('cv')
          ? {
              required
            }
          : {},
        videoLink: {
          required: requiredIf(() => {
            return this.isRequired('video') && !this.form.video;
          }),
          urlValidator
        },
        video: {
          required: requiredIf(() => {
            return this.isRequired('video') && !this.form.videoLink;
          })
        },
        reCaptchaResponse: {
          required
        },
        answers: {
          $each: {
            answer: {
              required
            }
          }
        }
      }
    };
  },
  mounted() {
    if (this.user) {
      this.fillUserData();
    }
  },
  methods: {
    isRequired(name) {
      return this.job.criterias.includes(name);
    },
    fillUserData() {
      this.form.email = this.user.email;
      this.form.name = this.user.fullName;
      this.form.phone = this.user.applicant.phone;
      this.form.title = this.user.applicant.position;

      if (this.isRequired('linkedin')) {
        this.form.linkedin = this.user.applicant.linkedin;
      }

      if (this.isRequired('github')) {
        this.form.github = this.user.applicant.github;
      }

      if (this.isRequired('portfolio')) {
        this.form.portfolioLink = this.user.applicant.website;
      }

      if (this.isRequired('cv')) {
        this.form.cv = this.user.applicant.cv;
      }

      if (this.isRequired('video')) {
        this.form.video = this.user.applicant.video;
        this.form.videoLink = this.user.applicant.videoLink;
      }
    },
    async onSubmit() {
      if (this.isRequesting) {
        return;
      }

      this.$v.form.$touch(); // will trigger errors to appear.

      if (this.$v.form.$error) {
        return;
      }

      if (this.isVideoInvalid) {
        return;
      }

      this.isRequesting = true;
      this.errorMessage = undefined;

      const formData = new FormData();

      for (const name in this.form) {
        if (this.form[name]) {
          if (name === 'cv' || name === 'video') {
            this.form[name] && formData.append(name, this.form[name].id);
          } else {
            formData.append(
              name,
              Array.isArray(this.form[name]) ? JSON.stringify(this.form[name]) : this.form[name]
            );
          }
        }
      }

      try {
        // console.log(formData)
        await this.$axios.post(`/jobs/${this.job.id}/apply`, formData);

        this.$emit('submitted', this.form);

        this.$gtm.push({
          event: 'ga.event',
          category: 'form',
          action: 'ApplicantSubmit',
          label: this.job.id
        });
        this.$gtm.push({ event: 'fb', fbEvent: 'InterestedSubmit' });

        if (document) {
          const trackingNode = document.createElement('div');
          trackingNode.innerHTML =
            '<img height="1" width="1" style="display:none;" alt="" src="https://dc.ads.linkedin.com/collect/?pid=226868&conversionId=327012&fmt=gif" /><img height="1" width="1" style="display:none;" alt="" src="https://conv.indeed.com/pagead/conv/6024391486407447/?script=0" />';
          document.body.appendChild(trackingNode);
        }

        // this.form = {
        //   ...initial
        // }
        //
        // this.$v.$reset()
      } catch (err) {
        this.errorMessage = err.message;
      }

      if (this.$refs.reCaptcha) {
        this.$refs.reCaptcha.reset();
      }

      this.isRequesting = false;
    },
    onVerify(response) {
      this.form.reCaptchaResponse = response;
      this.$v.form.reCaptchaResponse.$touch();
    },
    onExpired() {
      this.form.reCaptchaResponse = undefined;
      this.$v.form.reCaptchaResponse.$touch();
    }
  }
};
</script>
<style scoped lang="scss">
.form-item {
  &__error {
    font-size: 0.85rem;
    color: $red;
    text-align: left;
    opacity: 0.9;

    &__inner {
      padding-top: 0.5rem;
    }
  }
}
</style>
